import { TRAINING_GATE } from '../../constants';
import { canTrainChild, trainingRequirements } from '../../objectTree';

export const ROOT_PATH_LABEL = 'Root level';

/**
 * Ancestor names of `object`, root first, joined for display ("Tree › Conifer").
 * Walks `parent_uuid` through the flat `objects` list; a cycle or a missing
 * parent simply ends the walk.
 */
export const buildObjectPath = (object, objects = []) => {
	const byUuid = new Map(objects.map(node => [node.uuid, node]));
	const names = [];
	const seen = new Set();
	let parent = byUuid.get(object?.parent_uuid);
	while (parent && !seen.has(parent.uuid)) {
		seen.add(parent.uuid);
		names.unshift(parent.name);
		parent = byUuid.get(parent.parent_uuid);
	}
	return names.length ? names.join(' › ') : ROOT_PATH_LABEL;
};

/** Annotation figures for display: `1,840`. */
export const formatCount = value => Number(value ?? 0).toLocaleString();

/**
 * What the training gate shows for `object`, built on the tree helpers
 * (`trainingRequirements`, `canTrainChild`) so the tree card and the gate cannot
 * disagree. With no thresholds yet, a node not cleared to train is UNKNOWN, not
 * LOCKED. `needsNewAnnotations`: locked with every count met, so what is missing
 * is new annotations since the last training.
 *
 * @returns {{ status: string, annotations: object | null, tiles: object | null, needsNewAnnotations: boolean }}
 *   each requirement as `{ count, required, missing, met, percent }`
 */
export const getTrainingGate = object => {
	const requirements = trainingRequirements(object);
	const status = canTrainChild(object)
		? TRAINING_GATE.READY
		: requirements
			? TRAINING_GATE.LOCKED
			: TRAINING_GATE.UNKNOWN;

	return {
		status,
		annotations: requirements?.annotations ?? null,
		tiles: requirements?.tiles ?? null,
		needsNewAnnotations: status === TRAINING_GATE.LOCKED && requirements.countsMet,
	};
};

// POST /taxonomy/nodes caps `key` at 64 characters.
export const MAX_OBJECT_KEY_LENGTH = 64;
const KEY_SUFFIX_LENGTH = 5;

/**
 * Derive a taxonomy key from a display name, in the shape the API requires:
 * lower-case letters and digits with a single `_` between words, at most 64
 * characters. A short random suffix keeps two objects with the same name apart.
 */
export const buildObjectKey = name => {
	const suffix = Math.random()
		.toString(36)
		.slice(2, 2 + KEY_SUFFIX_LENGTH);
	const slug = String(name ?? '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.slice(0, MAX_OBJECT_KEY_LENGTH - KEY_SUFFIX_LENGTH - 1)
		.replace(/_+$/, '');
	return slug ? `${slug}_${suffix}` : suffix;
};
